import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mountain, ArrowLeft, Upload, Plus, X, MapPin, IndianRupee, Users, Calendar, Compass } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const trekTypes = ['Day Hike', 'Weekend Trek', 'Multi-day Trek', 'Expedition'];
const difficultyLevels = ['Easy', 'Moderate', 'Difficult', 'Extreme'];
const seasons = ['Spring (Mar-May)', 'Summer (Jun-Aug)', 'Autumn (Sep-Nov)', 'Winter (Dec-Feb)'];

const CreateTrekListing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    trek_type: '',
    difficulty: '',
    duration_days: '',
    max_altitude: '',
    best_season: [],
    start_point: { city: '', state: '' },
    end_point: { city: '', state: '' },
    itinerary: [],
    inclusions: [],
    exclusions: [],
    images: [],
    price_per_person: '',
    min_group_size: '1',
    max_group_size: '',
    upcoming_dates: []
  });
  const [imageUrls, setImageUrls] = useState(['']);
  const [newInclusion, setNewInclusion] = useState('');
  const [newExclusion, setNewExclusion] = useState('');
  const [newDate, setNewDate] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('supplier_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  useEffect(() => {
    const token = localStorage.getItem('supplier_token');
    if (!token) navigate('/supplier/onboarding');
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const toggleSeason = (season) => {
    setFormData(prev => ({
      ...prev,
      best_season: prev.best_season.includes(season)
        ? prev.best_season.filter(s => s !== season)
        : [...prev.best_season, season]
    }));
  };

  const addInclusion = () => {
    if (newInclusion.trim()) {
      setFormData(prev => ({ ...prev, inclusions: [...prev.inclusions, newInclusion.trim()] }));
      setNewInclusion('');
    }
  };

  const addExclusion = () => {
    if (newExclusion.trim()) {
      setFormData(prev => ({ ...prev, exclusions: [...prev.exclusions, newExclusion.trim()] }));
      setNewExclusion('');
    }
  };

  const addDate = () => {
    if (newDate) {
      setFormData(prev => ({ ...prev, upcoming_dates: [...prev.upcoming_dates, newDate] }));
      setNewDate('');
    }
  };

  const addImageUrl = () => setImageUrls(prev => [...prev, '']);
  const updateImageUrl = (index, value) => {
    setImageUrls(prev => { const updated = [...prev]; updated[index] = value; return updated; });
  };
  const removeImageUrl = (index) => setImageUrls(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.trek_type || !formData.difficulty) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!formData.price_per_person || !formData.max_group_size) {
      toast.error('Please enter pricing and group size');
      return;
    }

    const validImages = imageUrls.filter(url => url.trim() !== '');
    if (validImages.length === 0) {
      toast.error('Please add at least one image');
      return;
    }

    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        images: validImages,
        videos: [],
        duration_days: parseInt(formData.duration_days) || 1,
        max_altitude: parseInt(formData.max_altitude) || 0,
        price_per_person: parseFloat(formData.price_per_person),
        min_group_size: parseInt(formData.min_group_size) || 1,
        max_group_size: parseInt(formData.max_group_size),
        itinerary: formData.itinerary.length > 0 ? formData.itinerary : [{ day: 1, description: 'Trek day' }]
      };

      const response = await fetch(`${API_URL}/api/supplier/listings/treks`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Trek listing created successfully!');
        navigate('/supplier/dashboard');
      } else {
        toast.error(data.detail || 'Failed to create listing');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => navigate('/supplier/dashboard')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-5 h-5" /> Back to Dashboard
            </button>
            <div className="flex items-center gap-2">
              <Mountain className="h-6 w-6 text-[#E67E22]" />
              <span className="font-bold text-[#E67E22]">GoHimalayan</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{fontFamily: 'Playfair Display, serif'}}>
          Add Your Trek
        </h1>
        <p className="text-gray-600 mb-8">List your trekking expedition on GoHimalayan</p>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Trek Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trek Name *</label>
                <Input name="title" value={formData.title} onChange={handleInputChange} placeholder="e.g., Kedarkantha Summit Trek" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe the trek, highlights, what to expect..."
                  className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trek Type *</label>
                  <div className="flex flex-wrap gap-2">
                    {trekTypes.map(type => (
                      <button key={type} type="button" onClick={() => setFormData(prev => ({ ...prev, trek_type: type.toLowerCase().replace(' ', '_') }))}
                        className={`px-3 py-1.5 rounded-full text-sm ${formData.trek_type === type.toLowerCase().replace(' ', '_') ? 'bg-[#E67E22] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty *</label>
                  <div className="flex flex-wrap gap-2">
                    {difficultyLevels.map(level => (
                      <button key={level} type="button" onClick={() => setFormData(prev => ({ ...prev, difficulty: level.toLowerCase() }))}
                        className={`px-3 py-1.5 rounded-full text-sm ${formData.difficulty === level.toLowerCase() ? 'bg-[#E67E22] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                        {level}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (Days) *</label>
                  <Input type="number" name="duration_days" value={formData.duration_days} onChange={handleInputChange} placeholder="e.g., 5" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Altitude (meters)</label>
                  <Input type="number" name="max_altitude" value={formData.max_altitude} onChange={handleInputChange} placeholder="e.g., 3800" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Best Seasons</label>
                <div className="flex flex-wrap gap-2">
                  {seasons.map(season => (
                    <button key={season} type="button" onClick={() => toggleSeason(season)}
                      className={`px-3 py-1.5 rounded-full text-sm ${formData.best_season.includes(season) ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                      {season}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Start & End Points */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><Compass className="w-5 h-5" /> Route</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium mb-2">Start Point</h3>
                <Input name="start_point.city" value={formData.start_point.city} onChange={handleInputChange} placeholder="City" className="mb-2" />
                <Input name="start_point.state" value={formData.start_point.state} onChange={handleInputChange} placeholder="State" />
              </div>
              <div>
                <h3 className="font-medium mb-2">End Point</h3>
                <Input name="end_point.city" value={formData.end_point.city} onChange={handleInputChange} placeholder="City" className="mb-2" />
                <Input name="end_point.state" value={formData.end_point.state} onChange={handleInputChange} placeholder="State" />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><IndianRupee className="w-5 h-5" /> Pricing & Group Size</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price per Person (₹) *</label>
                <Input type="number" name="price_per_person" value={formData.price_per_person} onChange={handleInputChange} placeholder="e.g., 8500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Group Size</label>
                <Input type="number" name="min_group_size" value={formData.min_group_size} onChange={handleInputChange} placeholder="e.g., 4" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Group Size *</label>
                <Input type="number" name="max_group_size" value={formData.max_group_size} onChange={handleInputChange} placeholder="e.g., 15" />
              </div>
            </div>
          </div>

          {/* Inclusions/Exclusions */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium mb-2">Inclusions</h3>
                <div className="flex gap-2 mb-2">
                  <Input value={newInclusion} onChange={(e) => setNewInclusion(e.target.value)} placeholder="e.g., Meals, Camping gear" />
                  <Button type="button" onClick={addInclusion} variant="outline">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.inclusions.map((item, i) => (
                    <span key={i} className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-sm flex items-center gap-1">
                      {item} <X className="w-3 h-3 cursor-pointer" onClick={() => setFormData(prev => ({ ...prev, inclusions: prev.inclusions.filter((_, idx) => idx !== i) }))} />
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-medium mb-2">Exclusions</h3>
                <div className="flex gap-2 mb-2">
                  <Input value={newExclusion} onChange={(e) => setNewExclusion(e.target.value)} placeholder="e.g., Travel insurance" />
                  <Button type="button" onClick={addExclusion} variant="outline">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.exclusions.map((item, i) => (
                    <span key={i} className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-sm flex items-center gap-1">
                      {item} <X className="w-3 h-3 cursor-pointer" onClick={() => setFormData(prev => ({ ...prev, exclusions: prev.exclusions.filter((_, idx) => idx !== i) }))} />
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><Upload className="w-5 h-5" /> Trek Images</h2>
            <div className="space-y-3">
              {imageUrls.map((url, index) => (
                <div key={index} className="flex gap-2">
                  <Input value={url} onChange={(e) => updateImageUrl(index, e.target.value)} placeholder="https://example.com/image.jpg" className="flex-1" />
                  {imageUrls.length > 1 && <button type="button" onClick={() => removeImageUrl(index)} className="p-2 text-red-500"><X className="w-5 h-5" /></button>}
                </div>
              ))}
              <button type="button" onClick={addImageUrl} className="flex items-center gap-2 text-[#E67E22] hover:underline text-sm"><Plus className="w-4 h-4" /> Add another image</button>
            </div>
          </div>

          {/* Upcoming Dates */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><Calendar className="w-5 h-5" /> Upcoming Trek Dates</h2>
            <div className="flex gap-2 mb-4">
              <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
              <Button type="button" onClick={addDate} variant="outline">Add Date</Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.upcoming_dates.map((date, i) => (
                <span key={i} className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  {new Date(date).toLocaleDateString()} <X className="w-3 h-3 cursor-pointer" onClick={() => setFormData(prev => ({ ...prev, upcoming_dates: prev.upcoming_dates.filter((_, idx) => idx !== i) }))} />
                </span>
              ))}
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/supplier/dashboard')} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-[#E67E22] hover:bg-[#d35400]">{loading ? 'Creating...' : 'Create Trek Listing'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTrekListing;
